import type { DetailedProgress, PaginatedProgressResponse, UserProgressFilters, TrackingStatus, CourseTrackingSortField, UserCourseMetrics } from '@/services/tracking.service';
import { trackingService } from '@/services/tracking.service';
import { userService } from '@/services/user.service';
import { exportService, type ExportUserCourseProgressParams } from '@/services/export.service';
import { toast } from '@/components/ui/Toast';
import {
  AlertCircle,
  Book,
  BookOpen,
  Building,
  Calendar,
  CheckCircle,
  ChevronLeft,
  Clock,
  Download,
  Eye,
  FileDown,
  Loader2,
  Play,
  Search,
  TrendingUp,
  User,
  XCircle,
  Briefcase
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Modal from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import Pagination from '@/components/ui/Pagination';
import { useForm } from 'react-hook-form';
import { useDebounce, DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce';

interface UserInfo {
  name: string;
  _id: string;
  given_name: string;
  family_name: string;
  email: string;
  profile_image?: string;
  department?: string;
  departments?: string[];
  role?: string;
  roles?: string[];
  status: string;
  courses?: {
    assigned: number;
    completed: number;
    in_progress: number;
  };
}

interface FilterFormValues {
  filterStatus: string;
  sortBy: string;
}

export default function UserCourseList() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [courses, setCourses] = useState<DetailedProgress[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('last_accessed_at');

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Metrics from API
  const [metrics, setMetrics] = useState<UserCourseMetrics | null>(null);

  // Export loading state
  const [exportLoading, setExportLoading] = useState<{
    excel: boolean;
    csv: boolean;
  }>({ excel: false, csv: false });

  // Debounced search query
  const searchQuery = useDebounce(searchInput, DEFAULT_DEBOUNCE_DELAY);

  // Modal states
  const [showDepartmentsModal, setShowDepartmentsModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);

  // Form setup for Select components
  const { control, watch } = useForm<FilterFormValues>({
    defaultValues: {
      filterStatus: 'all',
      sortBy: 'last_accessed_at'
    }
  });

  // Watch form changes and sync with state
  useEffect(() => {
    const subscription = watch((value) => {
      if (value.filterStatus !== undefined) {
        setFilterStatus(value.filterStatus);
      }
      if (value.sortBy !== undefined) {
        setSortBy(value.sortBy);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filterStatus, sortBy, searchQuery, limit]);

  // Fetch data when userId, page, or filters change
  useEffect(() => {
    fetchData();
  }, [userId, page, limit, filterStatus, sortBy, searchQuery]);

  // Handle dynamic back navigation
  const handleBackNavigation = () => {
    navigate(-1)
  };

  // Fetch metrics (only when userId changes)
  const fetchMetrics = async () => {
    if (!userId) return;
    try {
      const metricsResponse = await trackingService.getUserMetrics(userId);
      setMetrics(metricsResponse);
    } catch (err: any) {
      console.error('Error fetching metrics:', err);
    }
  };

  // Fetch metrics when userId changes
  useEffect(() => {
    fetchMetrics();
  }, [userId]);

  const fetchData = async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      // Build filters for POST request
      const filters: UserProgressFilters = {
        page,
        limit,
        sort_by: sortBy as CourseTrackingSortField,
        sort_order: 'desc'
      };

      // Add search if present
      if (searchQuery) {
        filters.search = searchQuery;
      }

      // Add status filter if not 'all'
      if (filterStatus !== 'all') {
        filters.status = filterStatus as TrackingStatus;
      }

      // Fetch user info and courses in parallel
      const [userResponse, coursesResponse] = await Promise.all([
        userService.getUserById(userId),
        trackingService.getUserProgress(userId, filters)
      ]);

      // Process user info
      if (userResponse?.data) {
        setUserInfo(userResponse.data);
      }

      // Process courses - handle paginated response
      if (coursesResponse) {
        // Check if it's a paginated response (has 'total' and 'data' properties)
        if ('total' in coursesResponse && 'data' in coursesResponse) {
          const paginatedResponse = coursesResponse as PaginatedProgressResponse;
          setCourses(paginatedResponse.data || []);
          setTotalItems(paginatedResponse.total || 0);
          setTotalPages(Math.ceil((paginatedResponse.total || 0) / limit));
        } else if (Array.isArray(coursesResponse)) {
          // Fallback for array response
          setCourses(coursesResponse);
          setTotalPages(1);
          setTotalItems(coursesResponse.length);
        } else if (typeof coursesResponse === 'object') {
          // Single object response, wrap in array
          setCourses([coursesResponse as DetailedProgress]);
          setTotalPages(1);
          setTotalItems(1);
        } else {
          setCourses([]);
          setTotalPages(1);
          setTotalItems(0);
        }
      } else {
        setCourses([]);
        setTotalPages(1);
        setTotalItems(0);
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Failed to load user courses');
    } finally {
      setLoading(false);
    }
  };

  // Handle export functionality
  const handleExport = async (format: 'csv' | 'excel') => {
    if (!userId) return;

    try {
      setExportLoading(prev => ({ ...prev, [format]: true }));

      // Build export filters from current UI filters
      const exportFilters: ExportUserCourseProgressParams = {
        sort_order: 'desc'
      };

      // Add search filter
      if (searchQuery) {
        exportFilters.search = searchQuery;
      }

      // Add status filter
      if (filterStatus !== 'all') {
        exportFilters.status = filterStatus as ExportUserCourseProgressParams['status'];
      }

      // Add sort filter - map to API expected values
      if (sortBy) {
        const allowedSortFields: ExportUserCourseProgressParams['sort_by'][] = [
          'last_accessed_at',
          'progress_percentage',
          'deadline_date',
          'created_at',
        ];

        if (allowedSortFields.includes(sortBy as ExportUserCourseProgressParams['sort_by'])) {
          exportFilters.sort_by = sortBy as ExportUserCourseProgressParams['sort_by'];
        }
      }


      // Get user name for filename
      const userName = userInfo?.name || userInfo?.given_name || 'user';

      // Call export service with userName for filename
      const result = await exportService.exportUserCourseProgress(format, userId, userName, exportFilters);

      if (!result.success) {
        toast.error(result.message || 'No data available to export');
        return;
      }

      toast.success(`${result.count} course records exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error(`Failed to export data as ${format.toUpperCase()}`);
      console.error('Export error:', error);
    } finally {
      setExportLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'text-green-600 bg-green-100';
      case 'IN_PROGRESS':
        return 'text-blue-600 bg-blue-100';
      case 'NOT_STARTED':
        return 'text-gray-600 bg-gray-100';
      case 'EXPIRED':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4" />;
      case 'IN_PROGRESS':
        return <Play className="w-4 h-4" />;
      case 'NOT_STARTED':
        return <AlertCircle className="w-4 h-4" />;
      case 'EXPIRED':
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatTimeToHHMMSS = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Courses are now server-side filtered and sorted

  // Select options
  const statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Not Started', value: 'NOT_STARTED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Expired', value: 'EXPIRED' }
  ];

  const sortOptions = [
    { label: 'Last Accessed', value: 'last_accessed_at' },
    { label: 'Progress', value: 'progress_percentage' },
    { label: 'Deadline', value: 'deadline_date' },
    { label: 'Created', value: 'created_at' }
  ];

  // Statistics from metrics API
  const stats = {
    total: metrics?.total_courses ?? 0,
    mandatory: metrics?.mandatory_courses ?? 0,
    completed: metrics?.completed_courses ?? 0,
    inProgress: metrics?.in_progress_courses ?? 0,
    notStarted: metrics?.not_started_courses ?? 0,
    expired: metrics?.expired_courses ?? 0,
    averageProgress: metrics?.avg_progress_percentage ?? 0,
    totalTime: metrics?.total_time_invested_seconds ?? 0,
    completionRate: metrics?.completion_rate ?? 0
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
            <div>
              <div className="h-8 w-64 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-48 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>

        {/* User Info Card Skeleton */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
            <div className="flex-1 space-y-3">
              <div className="h-5 w-48 bg-gray-200 rounded"></div>
              <div className="h-4 w-64 bg-gray-200 rounded"></div>
              <div className="flex items-center gap-4">
                <div className="h-6 w-32 bg-gray-200 rounded-full"></div>
                <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
                <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className="w-8 h-8 bg-gray-200 rounded"></div>
                <div className="h-8 w-16 bg-gray-200 rounded"></div>
              </div>
              <div className="h-4 w-32 bg-gray-200 rounded mb-3"></div>
              <div className="h-2 w-full bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>

        {/* Filters Skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap gap-4">
            <div className="w-48 h-11 bg-gray-200 rounded-lg"></div>
            <div className="w-48 h-11 bg-gray-200 rounded-lg"></div>
            <div className="w-48 h-11 bg-gray-200 rounded-lg"></div>
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <th key={i} className="px-6 py-3">
                      <div className="h-3 bg-gray-200 rounded w-20 mx-auto"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[1, 2, 3, 4, 5].map((row) => (
                  <tr key={row}>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="h-4 w-48 bg-gray-200 rounded"></div>
                        <div className="h-3 w-20 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 w-24 bg-gray-200 rounded-full mx-auto"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="h-2 w-full bg-gray-200 rounded"></div>
                        <div className="h-4 w-12 bg-gray-200 rounded"></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 w-16 bg-gray-200 rounded mx-auto"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 w-24 bg-gray-200 rounded mx-auto"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 w-24 bg-gray-200 rounded mx-auto"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-8 w-8 bg-gray-200 rounded-lg mx-auto"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-gray-900 font-medium mb-1">Failed to Load</p>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackNavigation}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Go back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Course List</h1>
            <p className="text-gray-600 mt-1">
              Viewing courses for {userInfo?.name}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('excel')}
            disabled={exportLoading.excel}
            className="flex items-center gap-2 px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export as Excel"
          >
            {exportLoading.excel ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">
              {exportLoading.excel ? 'Exporting...' : 'Excel'}
            </span>
          </button>
          <button
            onClick={() => handleExport('csv')}
            disabled={exportLoading.csv}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export as CSV"
          >
            {exportLoading.csv ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">
              {exportLoading.csv ? 'Exporting...' : 'CSV'}
            </span>
          </button>
        </div>
      </div>

      {/* User Info Card */}
      {userInfo && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-xl font-semibold">
              {userInfo.profile_image ? (
                <img src={userInfo.profile_image} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
               `${userInfo.name.split(" ")[0][0]}${userInfo.name.split(" ")[userInfo.name.split(" ").length - 1][0]}`
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">
                {userInfo.given_name} {userInfo.family_name}
              </h2>
              <p className="text-gray-600">{userInfo.email}</p>
              <div className="flex items-center flex-wrap gap-4 mt-2 text-sm">
                {/* Departments */}
                {(() => {
                  const departments = userInfo.departments || (userInfo.department ? [userInfo.department] : []);
                  if (departments.length === 0) {
                    return null;
                  }

                  if (departments.length === 1) {
                    return (
                      <span className="flex items-center gap-1">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                          {departments[0]}
                        </span>
                      </span>
                    );
                  }

                  return (
                    <span className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-gray-400" />
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                        {departments[0]}
                      </span>
                      <button
                        onClick={() => setShowDepartmentsModal(true)}
                        className="text-xs text-brand-blue-600 font-medium hover:text-brand-blue-800 hover:underline"
                      >
                        +{departments.length - 1} more
                      </button>
                    </span>
                  );
                })()}

                {/* Roles */}
                {(() => {
                  const roles = userInfo.roles || (userInfo.role ? [userInfo.role] : []);

                  if (roles.length === 0) {
                    return null;
                  }

                  if (roles.length === 1) {
                    return (
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full">
                          {roles[0]}
                        </span>
                      </span>
                    );
                  }

                  return (
                    <span className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full">
                        {roles[0]}
                      </span>
                      <button
                        onClick={() => setShowRolesModal(true)}
                        className="text-xs text-brand-blue-600 font-medium hover:text-brand-blue-800 hover:underline"
                      >
                        +{roles.length - 1} more
                      </button>
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Book className="w-8 h-8 text-brand-blue-600" />
            <span className="text-2xl font-bold text-gray-900">{stats.total}</span>
          </div>
          <p className="text-gray-600 text-sm">Total Courses</p>
          <div className="mt-3 text-xs text-gray-500">
            {stats.mandatory} mandatory
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-8 h-8 text-brand-green-600" />
            <span className="text-2xl font-bold text-gray-900">{stats.completed}</span>
          </div>
          <p className="text-gray-600 text-sm">Completed</p>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full"
                style={{ width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 text-primary-600" />
            <span className="text-2xl font-bold text-gray-900">
              {Math.round(stats.averageProgress)}%
            </span>
          </div>
          <p className="text-gray-600 text-sm">Average Progress</p>
          <div className="mt-3 text-xs text-gray-500">
            {stats.inProgress} in progress
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Clock className="w-8 h-8 text-brand-purple-600" />
            <span className="text-2xl font-bold text-gray-900">
              {formatTimeToHHMMSS(stats.totalTime)}
            </span>
          </div>
          <p className="text-gray-600 text-sm">Time Invested</p>
          <div className="mt-3 text-xs text-gray-500">
            Total: {formatTime(stats.totalTime)}
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by course title..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm min-h-[42px]"
                style={{ fontSize: '0.875rem' }}
              />
            </div>
          </div>

          <div className="w-48">
            <Select
              name="filterStatus"
              control={control}
              options={statusOptions}
              isSearchable={false}
              placeholder="Filter by status"
            />
          </div>

          <div className="w-48">
            <Select
              name="sortBy"
              control={control}
              options={sortOptions}
              isSearchable={false}
              placeholder="Sort by"
            />
          </div>
        </div>
      </div>

      {/* Course List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Spent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deadline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completed At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Accessed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {courses.map((course) => (
                <tr key={course._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {course.course_title || course.course_id}
                      </p>
                      {/* <p className="text-xs text-gray-500 mt-1">
                        {course.course_id}
                      </p> */}
                      {course.is_mandatory && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 mt-1">
                          Mandatory
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(course.status || '')}`}>
                      {getStatusIcon(course.status || '')}
                      {(course.status || 'N/A').replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              course.status === 'COMPLETED' ? 'bg-green-600' :
                              course.status === 'IN_PROGRESS' ? 'bg-blue-600' :
                              'bg-gray-400'
                            }`}
                            style={{ width: `${course.progress_percentage || 0}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {course.progress_percentage || 0}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatTimeToHHMMSS(course.total_time_spent_seconds || 0)}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {course.deadline_date ? (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className={
                          new Date(course.deadline_date) < new Date() ? 'text-red-600' : 'text-gray-600'
                        }>
                          {formatDate(course.deadline_date)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">No deadline</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(course.created_at)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(course.started_at)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(course.completed_at)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(course.last_accessed_at)}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => navigate(
                        `/admin/course/assignments/user/${userId}/course/${course.course_id}/progress`,
                        { state: { from: location.pathname } }
                      )}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {courses.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No courses found</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={limit}
            onPageChange={setPage}
            onItemsPerPageChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
            showItemsPerPage={true}
          />
        )}
      </div>

      {/* Departments Modal */}
      {userInfo && (
        <Modal
          isOpen={showDepartmentsModal}
          onClose={() => setShowDepartmentsModal(false)}
          title="Department Details"
          subtitle={`${userInfo.name} (${userInfo.email})`}
          size="md"
          maxHeight="80vh"
          footer={
            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowDepartmentsModal(false)}
                className="px-4 py-2 bg-brand-blue-600 text-white rounded-lg hover:bg-brand-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          }
        >
          <div className="space-y-3">
            {(userInfo.departments || (userInfo.department ? [userInfo.department] : [])).map((dept, idx) => (
              <div key={`dept-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-brand-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{dept}</h3>
                  </div>
                  <Building className="w-5 h-5 text-gray-500" />
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Roles Modal */}
      {userInfo && (
        <Modal
          isOpen={showRolesModal}
          onClose={() => setShowRolesModal(false)}
          title="Role Details"
          subtitle={`${userInfo.name} (${userInfo.email})`}
          size="md"
          maxHeight="80vh"
          footer={
            <div className="flex items-center justify-end">
              <button
                onClick={() => setShowRolesModal(false)}
                className="px-4 py-2 bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 transition-colors"
              >
                Close
              </button>
            </div>
          }
        >
          <div className="space-y-3">
            {(userInfo.roles || (userInfo.role ? [userInfo.role] : [])).map((role, idx) => (
              <div key={`role-${idx}`} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-brand-purple-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">{role}</h3>
                  </div>
                  <Briefcase className="w-5 h-5 text-gray-500" />
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}